import { useState } from "react";
import { AlertTriangle, Lock, ShieldCheck } from "lucide-react";
import { C, inputStyle } from "./theme.js";
import * as vault from "./lib/keyvault.js";
import { normalizeOwnerId } from "./lib/crypto.js";

/* ============================================================================
   הקמת ההצפנה לחשבון.

   הכספת נפתחת בתעודת הזהות של בעל הנכס — לא בסיסמה נפרדת: הת"ז כבר
   מזהה את בעל החשבון במחיקה ובשחזור, היא לא משתנה ולא נשכחת, וכך
   הצילומים נפתחים לבעל הנכס תמיד. שני מסכים: אישור הת"ז, ואז הצגת קוד
   השחזור פעם אחת — הוא הגיבוי היחיד למקרה של טעות בהקלדת הת"ז.
   ============================================================================ */

export default function VaultSetup({ onClose, ownerId = "" }) {
  const [step, setStep] = useState("id"); // id | code
  const [idNumber, setIdNumber] = useState(() => normalizeOwnerId(ownerId));
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const digits = normalizeOwnerId(idNumber);

  const create = async (e) => {
    e.preventDefault();
    if (digits.length < 5) { setErr("תעודת זהות קצרה מדי."); return; }
    setBusy(true); setErr("");
    try {
      setCode(await vault.setupKeys(digits));
      setStep("code");
    } catch (ex) {
      setErr(ex.message || "ההקמה נכשלה");
    }
    setBusy(false);
  };

  return (
    <div dir="rtl" className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(20,38,43,0.45)" }}>
      <div className="w-full max-w-md rounded-2xl border p-6"
        style={{ background: C.card, borderColor: C.line }}>

        {step === "id" ? (
          <form onSubmit={create}>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={18} style={{ color: C.teal }} />
              <h2 className="text-lg font-bold">הצפנת צילומי תעודות זהות</h2>
            </div>
            <p className="text-sm mb-4" style={{ color: C.muted }}>
              צילומי תעודות הזהות של הדיירים והערבים יוצפנו במפתח שקיים רק
              בדפדפן שלכם — גם למי שמפעיל את השרת אין דרך לפתוח אותם.
              הצילומים ייפתחו לכם עם תעודת הזהות שלכם, בלי סיסמה נוספת.
            </p>

            <label className="text-[12px]" style={{ color: C.muted }}>
              תעודת הזהות של בעל הנכס — היא שתפתח את הצילומים
            </label>
            <input inputMode="numeric" dir="ltr" autoFocus
              value={idNumber} onChange={(e) => { setIdNumber(e.target.value); setErr(""); }}
              placeholder="9 ספרות" style={inputStyle} required />
            {ownerId && digits !== normalizeOwnerId(ownerId) && (
              <div className="mt-1 text-[11px]" style={{ color: C.ochre }}>
                שימו לב: זו אינה תעודת הזהות שב"פרטי בעל הנכס" — הפתיחה האוטומטית תעבוד רק כשהן זהות.
              </div>
            )}

            {err && (
              <div className="mt-2 rounded-lg p-2.5 text-sm" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
                {err}
              </div>
            )}

            <div className="mt-4 rounded-lg p-3 text-[12px] flex gap-2"
              style={{ background: "#FBF2E3", color: "#6B4E12" }}>
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                אם תעודת הזהות תוקלד כאן שגויה, רק קוד השחזור שיוצג מיד יוכל
                לפתוח את הצילומים. בלי אף אחד מהם הם אבודים לצמיתות — לשרת אין עותק.
              </span>
            </div>

            <button type="submit" disabled={busy || digits.length < 5}
              className="mt-4 w-full rounded-xl py-3 text-sm"
              style={{ background: C.teal, color: "#fff", opacity: busy || digits.length < 5 ? 0.6 : 1 }}>
              {busy ? "מייצר מפתחות…" : "הפעלת ההצפנה"}
            </button>

            <button type="button" onClick={onClose}
              className="mt-2 w-full py-2 text-xs" style={{ color: C.muted }}>
              לא עכשיו
            </button>
          </form>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Lock size={18} style={{ color: C.teal }} />
              <h2 className="text-lg font-bold">קוד השחזור</h2>
            </div>
            <p className="text-sm mb-3" style={{ color: C.muted }}>
              שמרו אותו במקום בטוח מחוץ למערכת. הוא לא יוצג שוב, ואין דרך לייצר
              אותו מחדש.
            </p>

            <div className="rounded-lg border p-4 text-center"
              style={{ borderColor: C.line, background: "#FBFCFB" }}>
              <div className="text-xl font-bold" style={{ letterSpacing: 2, direction: "ltr" }}>
                {code}
              </div>
            </div>

            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(code);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch { /* דפדפן שחוסם — אפשר לסמן ולהעתיק ידנית */ }
              }}
              className="mt-3 w-full rounded-lg py-2.5 text-sm"
              style={{ border: `1px solid ${C.line}` }}>
              {copied ? "הועתק" : "העתקת הקוד"}
            </button>

            <label className="flex items-start gap-2 text-sm mt-4">
              <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="mt-1" />
              <span>שמרתי את קוד השחזור במקום בטוח</span>
            </label>

            <button onClick={onClose} disabled={!saved}
              className="mt-4 w-full rounded-xl py-3 text-sm"
              style={{ background: C.teal, color: "#fff", opacity: saved ? 1 : 0.5 }}>
              סיום
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
